/**
 * The two pieces of chrome a networked match needs and a local one does not:
 * a connection indicator, and a scoreboard with everybody's ping on it.
 *
 * A SEPARATE OVERLAY, not an addition to src/render/hud.ts. The HUD is the
 * game's — score, clock, boost, the goal banner — and every one of those means
 * the same thing offline. Nothing here does. Keeping them apart is what lets
 * the offline game stay byte-for-byte the game it was, and it is the same
 * separation ./menu.ts keeps from the renderer.
 *
 * The visual language is ./style.ts's: dark, high contrast, uppercase tracked
 * labels, `tabular-nums` on every number so a ping going 99 -> 100 does not
 * shift the column it sits in.
 *
 * ===========================================================================
 * THE INDICATOR IS ALWAYS UP; THE SCOREBOARD IS HELD
 * ===========================================================================
 *
 * A ping you have to open a panel to read is a ping nobody reads, and the whole
 * point of showing it is that a player who is about to blame the physics should
 * be able to see the number without deciding to look for it. So the pill is
 * permanent, small, and in a corner — and it CHANGES COLOUR rather than
 * appearing, because something that appears mid-match is something that took
 * your eye off the ball at the exact moment the link got bad.
 *
 * The full table is held on Tab, the key every game in this genre uses for it.
 */

import { pingBand, pingLabel, sortedRoster, type NetPlayerRow } from './net-model';
import { MENU_Z_INDEX } from './style';

/** One frame's worth of connection state. Everything is already computed. */
export interface NetHudState {
  pingMs: number;
  degraded: boolean;
  /** Ticks the prediction is ahead of the newest snapshot. */
  lead: number;
  /** Milliseconds since the last snapshot, or -1 before the first. */
  staleMs: number;
  players: readonly NetPlayerRow[];
  score: readonly [number, number];
  teams: boolean;
}

const TEAM_NAMES = ['Blue', 'Orange'] as const;
/** Matches TEAM_COLORS in src/render/materials.ts; kept as text so this module
 *  stays clear of the Three.js layer, exactly as ./menu.ts does. */
const TEAM_HEX = ['#2a7dff', '#ff8a1f'] as const;

const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const NET_HUD_STYLE = `
.rw-net {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: ${MENU_Z_INDEX - 1};
  font-family: 'Inter', 'SF Pro Display', 'Segoe UI Variable Display', system-ui,
    -apple-system, 'Helvetica Neue', Arial, sans-serif;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  color: #eef4ff;
  -webkit-font-smoothing: antialiased;
}
.rw-net * { box-sizing: border-box; font-variant-numeric: tabular-nums; }

/* --- the pill ---------------------------------------------------------- */

.rw-net-pill {
  position: absolute;
  right: 18px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 11px;
  border-radius: 999px;
  background: rgba(6, 9, 16, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.06em;
}
.rw-net-dot { width: 7px; height: 7px; border-radius: 50%; background: #4fd08a; }
.rw-net-pill[data-band='fair'] .rw-net-dot { background: #ffc75a; }
.rw-net-pill[data-band='poor'] .rw-net-dot { background: #ff6b5a; }
.rw-net-pill[data-warn='on'] {
  border-color: rgba(255, 107, 90, 0.55);
  background: rgba(52, 10, 8, 0.72);
}
.rw-net-warn {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #ff9a8c;
}
.rw-net-lead { color: rgba(186, 204, 232, 0.55); font-weight: 600; }

/* --- the scoreboard ---------------------------------------------------- */

.rw-net-board {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 460px;
  max-width: 90vw;
  padding: 18px 20px 14px;
  border-radius: 10px;
  background: linear-gradient(160deg, rgba(6, 9, 16, 0.95), rgba(3, 5, 10, 0.97));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.rw-net-board[hidden] { display: none; }
.rw-net-score {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.rw-net-score b { font-size: 30px; font-weight: 800; }
.rw-net-score span {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}
.rw-net-head, .rw-net-row {
  display: grid;
  grid-template-columns: 1fr 74px 64px 58px;
  align-items: center;
  gap: 10px;
  padding: 5px 4px;
}
.rw-net-head {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.55);
  padding-top: 10px;
}
.rw-net-row { font-size: 13px; font-weight: 600; border-top: 1px solid rgba(255,255,255,0.04); }
.rw-net-row.me { background: rgba(255, 255, 255, 0.045); border-radius: 4px; }
.rw-net-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rw-net-name i {
  font-style: normal;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: rgba(150, 172, 205, 0.6);
  margin-left: 7px;
}
.rw-net-cell { font-size: 12px; color: rgba(186, 204, 232, 0.72); }
.rw-net-ping[data-band='good'] { color: #4fd08a; }
.rw-net-ping[data-band='fair'] { color: #ffc75a; }
.rw-net-ping[data-band='poor'] { color: #ff6b5a; }
.rw-net-hint {
  padding-top: 10px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(150, 172, 205, 0.45);
  text-align: center;
}
`;

/**
 * The scoreboard's rows as markup. Exported and pure so the layout is testable
 * without a DOM, which is the same trick ./training uses for its panels.
 */
export const scoreboardRowsMarkup = (state: NetHudState): string => {
  const rows = sortedRoster(state.players);
  if (rows.length === 0) {
    return `<div class="rw-net-row"><div class="rw-net-name">Waiting for the roster…</div></div>`;
  }
  return rows
    .map(player => {
      const classes = ['rw-net-row'];
      if (player.isLocal) classes.push('me');
      const tag = player.isBot ? '<i>BOT</i>' : player.carIndex === null ? '<i>SPEC</i>' : '';
      const team = state.teams
        ? `<div class="rw-net-cell" style="color:${TEAM_HEX[player.team]}">${TEAM_NAMES[player.team]}</div>`
        : `<div class="rw-net-cell">—</div>`;
      // A bot has no connection, so it gets a dash rather than a 0 ms that
      // would read as the best link in the room.
      const ping = player.isBot
        ? `<div class="rw-net-cell">—</div>`
        : `<div class="rw-net-cell rw-net-ping" data-band="${pingBand(player.ping)}">${pingLabel(player.ping)}</div>`;
      return (
        `<div class="${classes.join(' ')}">` +
        `<div class="rw-net-name">${esc(player.name)}${tag}</div>` +
        team +
        `<div class="rw-net-cell">${esc(player.car)}</div>` +
        ping +
        `</div>`
      );
    })
    .join('');
};

/** The pill's text, for the same reason. */
export const pillMarkup = (state: NetHudState): string => {
  const warn =
    state.staleMs > 250
      ? 'No Data'
      : state.degraded
        ? 'Unstable'
        : '';
  return (
    `<span class="rw-net-dot"></span>` +
    `<span>${pingLabel(state.pingMs)}</span>` +
    (warn.length > 0 ? `<span class="rw-net-warn">${warn}</span>` : '') +
    `<span class="rw-net-lead">${state.lead >= 0 ? '+' : ''}${Math.round(state.lead)}t</span>`
  );
};

// ---------------------------------------------------------------------------
// The overlay
// ---------------------------------------------------------------------------

export class NetHud {
  private readonly root: HTMLDivElement;
  private readonly styleEl: HTMLStyleElement;
  private readonly pill: HTMLElement;
  private readonly board: HTMLElement;
  private readonly rows: HTMLElement;
  private readonly scoreEl: HTMLElement;
  private readonly onKeyDown: (event: KeyboardEvent) => void;
  private readonly onKeyUp: (event: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  private open = false;
  /** Last markup written, so a 120 Hz render loop is not rewriting the DOM. */
  private pillSignature = '';
  private boardSignature = '';

  constructor(container: HTMLElement) {
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = NET_HUD_STYLE;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.className = 'rw-net';
    this.root.innerHTML = `
      <div class="rw-net-pill"></div>
      <div class="rw-net-board" hidden>
        <div class="rw-net-score"></div>
        <div class="rw-net-head">
          <div>Player</div><div>Team</div><div>Car</div><div>Ping</div>
        </div>
        <div class="rw-net-rows"></div>
        <div class="rw-net-hint">Hold Tab</div>
      </div>
    `;
    container.appendChild(this.root);

    const q = <T extends Element>(selector: string): T => {
      const el = this.root.querySelector(selector);
      if (!el) throw new Error(`net hud element missing: ${selector}`);
      return el as T;
    };
    this.pill = q('.rw-net-pill');
    this.board = q('.rw-net-board');
    this.rows = q('.rw-net-rows');
    this.scoreEl = q('.rw-net-score');

    // Tab moves focus and would walk the player out of the canvas, so it is
    // swallowed here rather than merely observed.
    this.onKeyDown = event => {
      if (event.code !== 'Tab') return;
      event.preventDefault();
      this.open = true;
    };
    this.onKeyUp = event => {
      if (event.code === 'Tab') this.open = false;
    };
    // A held Tab that loses focus never sends its keyup, which would leave the
    // board up over the match forever — the same class of bug as a stuck key.
    this.onBlur = () => {
      this.open = false;
    };
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  /** True while the player is holding the scoreboard open. */
  get scoreboardOpen(): boolean {
    return this.open;
  }

  update(state: NetHudState): void {
    const band = pingBand(state.pingMs);
    const warn = state.degraded || state.staleMs > 250;
    const markup = pillMarkup(state);
    const signature = `${markup}|${band}|${warn}`;
    if (signature !== this.pillSignature) {
      this.pillSignature = signature;
      this.pill.innerHTML = markup;
      this.pill.dataset.band = band;
      if (warn) this.pill.dataset.warn = 'on';
      else delete this.pill.dataset.warn;
    }

    this.board.hidden = !this.open;
    if (!this.open) return;

    const rows = scoreboardRowsMarkup(state);
    const score =
      `<span style="color:${TEAM_HEX[0]}">Blue</span><b>${state.score[0]}</b>` +
      `<b>${state.score[1]}</b><span style="color:${TEAM_HEX[1]}">Orange</span>`;
    const boardSignature = `${rows}|${score}`;
    if (boardSignature === this.boardSignature) return;
    this.boardSignature = boardSignature;
    this.rows.innerHTML = rows;
    this.scoreEl.innerHTML = score;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.root.remove();
    this.styleEl.remove();
  }
}
